import React, { createContext, useCallback, useContext, useState } from "react";
import { ToastNotification, ToastNotificationType } from "../../models/base/TosterNotification";


interface NotificationContextType {
    notifications: ToastNotification[];
    notify: (type: ToastNotificationType, message: string, title?: string) => void;
    removeNotification: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

    const [notifications, setNotifications] = useState<ToastNotification[]>([]);

    const removeNotification = useCallback((id: string) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    }, []);

    const notify = useCallback(
        (type:  ToastNotificationType, message: string, title?: string) => {
            const id = Math.random().toString(36).substr(2, 9); 

            const newNotification: ToastNotification = {
                id,
                type,
                message,
                title: title ?? (type === "success" ? "Success" : type === "error" ? "Error" : "Info"),
            };

            setNotifications(prev => [...prev, newNotification]);

            // auto close after 8s
            setTimeout(() => {
                removeNotification(id);
            }, 8000);
        },
        [removeNotification]
    );

    const values = {
        notifications,
        notify,
        removeNotification
    };

    return (
        <NotificationContext.Provider value={values}>
            {children}
        </NotificationContext.Provider>
    );
}

export const useNotification = (): NotificationContextType => {
    const context = useContext(NotificationContext);

    if (!context) {
        throw new Error("useNotification must be used within a NotificationProvider");
    }

    return context;
}